import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '../ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '../ui/alert-dialog';
import { Layers, FolderPlus, Trash2, GraduationCap, Pencil } from 'lucide-react';
import { createTeacherClass, updateTeacherClass, deleteTeacherClass } from '../../lib/api';
import { toast } from 'sonner';

const NIVEAUX = [
  { value: 'college', label: 'Collège', annees: ['6ème', '5ème', '4ème', '3ème'] },
  { value: 'lycee', label: 'Lycée', annees: ['Seconde', 'Première', 'Terminale'] },
  { value: 'postbac', label: 'Post-Bac', annees: ['BAC+1', 'BAC+2', 'BAC+3', 'BAC+4', 'BAC+5'] },
];

export { NIVEAUX };

export function ClassesSection({ teacherClasses, onRefresh }) {
  const [showAddClass, setShowAddClass] = useState(false);
  const [classForm, setClassForm] = useState({ nom: '', niveau: 'lycee', annee: '' });
  const [editingClass, setEditingClass] = useState(null);
  const [editForm, setEditForm] = useState({ nom: '', niveau: '', annee: '' });

  const availableAnnees = NIVEAUX.find(n => n.value === classForm.niveau)?.annees || [];
  const editAvailableAnnees = NIVEAUX.find(n => n.value === editForm.niveau)?.annees || [];
  const niveauLabel = (nv) => NIVEAUX.find(n => n.value === nv)?.label || nv;

  const handleAddClass = async () => {
    if (!classForm.nom || !classForm.niveau || !classForm.annee) { toast.error('Remplissez tous les champs'); return; }
    try {
      await createTeacherClass(classForm);
      toast.success(`Classe "${classForm.nom}" créée`);
      setClassForm({ nom: '', niveau: 'lycee', annee: '' });
      setShowAddClass(false);
      onRefresh();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur lors de la création"); }
  };

  const handleDeleteClass = async (classId) => {
    try { await deleteTeacherClass(classId); toast.success('Classe supprimée'); onRefresh(); }
    catch { toast.error('Erreur lors de la suppression'); }
  };

  const openEditDialog = (tc) => {
    setEditForm({ nom: tc.nom, niveau: tc.niveau || 'lycee', annee: tc.annee || '' });
    setEditingClass(tc);
  };

  const handleEditClass = async () => {
    if (!editForm.nom || !editForm.niveau || !editForm.annee) { toast.error('Remplissez tous les champs'); return; }
    try {
      await updateTeacherClass(editingClass.class_id, editForm);
      toast.success(`Classe "${editForm.nom}" mise à jour`);
      setEditingClass(null);
      onRefresh();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur lors de la modification"); }
  };

  return (
    <Card className="bg-card border-border border-primary/20" data-testid="classes-section">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-base flex items-center gap-2">
            <Layers className="w-5 h-5 text-primary" /> Mes Classes
          </CardTitle>
          <Dialog open={showAddClass} onOpenChange={setShowAddClass}>
            <DialogTrigger asChild>
              <Button data-testid="add-class-btn" size="sm" className="bg-primary text-primary-foreground hover:bg-blue-600">
                <FolderPlus className="w-4 h-4 mr-2" /> Nouvelle classe
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-card border-border">
              <DialogHeader><DialogTitle>Créer une classe</DialogTitle><DialogDescription>Renseignez les informations de votre nouvelle classe.</DialogDescription></DialogHeader>
              <div className="space-y-4">
                <div>
                  <Label>Nom de la classe</Label>
                  <Input data-testid="class-nom-input" placeholder="ex: STMG Terminal 1" value={classForm.nom} onChange={(e) => setClassForm(p => ({ ...p, nom: e.target.value }))} />
                </div>
                <div>
                  <Label className="flex items-center gap-1"><GraduationCap className="w-3.5 h-3.5" /> Niveau</Label>
                  <Select value={classForm.niveau} onValueChange={(v) => setClassForm(p => ({ ...p, niveau: v, annee: '' }))}>
                    <SelectTrigger data-testid="class-niveau-select"><SelectValue /></SelectTrigger>
                    <SelectContent>{NIVEAUX.map(n => <SelectItem key={n.value} value={n.value}>{n.label}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Année</Label>
                  <Select value={classForm.annee} onValueChange={(v) => setClassForm(p => ({ ...p, annee: v }))}>
                    <SelectTrigger data-testid="class-annee-select"><SelectValue placeholder="Sélectionnez l'année" /></SelectTrigger>
                    <SelectContent>{availableAnnees.map(a => <SelectItem key={a} value={a}>{a}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <Button data-testid="class-submit-btn" onClick={handleAddClass} className="w-full bg-primary text-primary-foreground hover:bg-blue-600">Créer la classe</Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </CardHeader>
      <CardContent>
        {teacherClasses.length > 0 ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {teacherClasses.map((tc) => (
              <div key={tc.class_id} className="flex items-center justify-between p-3 rounded-lg bg-secondary/50 border border-border group hover:border-primary/30 transition-colors" data-testid={`class-card-${tc.class_id}`}>
                <div className="min-w-0">
                  <p className="font-medium text-sm truncate">{tc.nom}</p>
                  <p className="text-xs text-muted-foreground">{niveauLabel(tc.niveau)} — {tc.annee}</p>
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  <Button
                    variant="ghost"
                    size="sm"
                    className="text-muted-foreground hover:text-primary"
                    onClick={() => openEditDialog(tc)}
                    data-testid={`edit-class-${tc.class_id}`}
                  >
                    <Pencil className="w-4 h-4" />
                  </Button>
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button variant="ghost" size="sm" className="text-muted-foreground hover:text-destructive" data-testid={`delete-class-${tc.class_id}`}>
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent className="bg-card border-border">
                      <AlertDialogHeader>
                        <AlertDialogTitle>Supprimer la classe</AlertDialogTitle>
                        <AlertDialogDescription>Supprimer la classe "{tc.nom}" ? Les élèves associés ne seront pas supprimés.</AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel>Annuler</AlertDialogCancel>
                        <AlertDialogAction onClick={() => handleDeleteClass(tc.class_id)} className="bg-destructive text-destructive-foreground hover:bg-destructive/90" data-testid={`confirm-delete-class-${tc.class_id}`}>Supprimer</AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-sm text-muted-foreground text-center py-4">Aucune classe créée. Créez vos classes pour organiser vos élèves et QCM.</p>
        )}
      </CardContent>

      {/* Edit Class Dialog */}
      <Dialog open={!!editingClass} onOpenChange={(open) => { if (!open) setEditingClass(null); }}>
        <DialogContent className="bg-card border-border">
          <DialogHeader><DialogTitle>Modifier la classe</DialogTitle><DialogDescription>Modifiez le nom, le niveau ou l'année de la classe.</DialogDescription></DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Nom de la classe</Label>
              <Input
                data-testid="edit-class-nom-input"
                placeholder="ex: STMG Terminal 1"
                value={editForm.nom}
                onChange={(e) => setEditForm(p => ({ ...p, nom: e.target.value }))}
              />
            </div>
            <div>
              <Label className="flex items-center gap-1"><GraduationCap className="w-3.5 h-3.5" /> Niveau</Label>
              <Select value={editForm.niveau} onValueChange={(v) => setEditForm(p => ({ ...p, niveau: v, annee: '' }))}>
                <SelectTrigger data-testid="edit-class-niveau-select"><SelectValue /></SelectTrigger>
                <SelectContent>{NIVEAUX.map(n => <SelectItem key={n.value} value={n.value}>{n.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>Année</Label>
              <Select value={editForm.annee} onValueChange={(v) => setEditForm(p => ({ ...p, annee: v }))}>
                <SelectTrigger data-testid="edit-class-annee-select"><SelectValue placeholder="Sélectionnez l'année" /></SelectTrigger>
                <SelectContent>{editAvailableAnnees.map(a => <SelectItem key={a} value={a}>{a}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <Button data-testid="edit-class-submit-btn" onClick={handleEditClass} className="w-full bg-primary text-primary-foreground hover:bg-blue-600">
              Enregistrer les modifications
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
